import { useMemo } from "react";
import { FlightSlotProvider } from "@b2bportal/core-flights";
import { defaultFlightSlots } from "@b2bportal/core-flights";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { PackageFlightCard } from "../PackageFlightCard";
import { PackageFlightDeparturePageHeader } from "../PackageFlightDeparturePageHeader";
import { PackageFlightFareGridItem } from "../PackageFlightFareGridItem";
import { PackageFlightFilterDialog } from "../PackageFlightFilterDialog";
import { PackageFlightReturnPageHeader } from "../PackageFlightReturnPageHeader";
import {
  defaultRbcSlots,
  RbcComponent,
  RbcSlotProvider,
} from "@/components/slots";

/**
 * Package Flight Slot Provider with nested RBC and Flight slot providers.
 *
 * ## Nested Provider Structure
 * This component uses a nested provider structure:
 * - **RbcSlotProvider** (outer): Provides RBC-wide components used across multiple funnels
 * - **FlightSlotProvider** (inner): Provides flight-specific components for this funnel
 *
 * See: apps/rbc/src/components/slots/RbcSlotProvider.tsx for more details
 * Docs: docs/apps/rbc.md#rbc-slot-provider-system
 */
export const PackagesFlightSlotProvider = ({ children }) => {
  const slotComponents = useMemo(
    () => ({
      // If there's components we want to override over the base flight components, we can slot them here
      // For example, we'll eventually update the flight cards to show the package's price
      ...defaultFlightSlots(),
      // we dont wan't flight shop search in the package flow
      [CoreFlightsComponent.FlightShopSearch]: () => null,
      [CoreFlightsComponent.FlightCard]: PackageFlightCard,
      [CoreFlightsComponent.FilterDialog]: PackageFlightFilterDialog,
    }),
    []
  );

  const rbcSlotComponents = useMemo(() => {
    return {
      ...defaultRbcSlots(),
      [RbcComponent.DeparturePageHeader]: PackageFlightDeparturePageHeader,
      [RbcComponent.ReturnPageHeader]: PackageFlightReturnPageHeader,
      [RbcComponent.FlightFareGridItem]: PackageFlightFareGridItem,
    };
  }, []);

  return (
    <RbcSlotProvider
      context={{
        components: rbcSlotComponents,
        providerName: "package-flights",
      }}
    >
      <FlightSlotProvider
        context={{
          components: slotComponents,
          providerName: "package-flights",
        }}
      >
        {children}
      </FlightSlotProvider>
    </RbcSlotProvider>
  );
};
